import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import enTranslations from '../translations/en';
import frTranslations from '../translations/fr';

const PremiumSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const t = language === 'fr' ? frTranslations : enTranslations;
  
  const returnTo = searchParams.get('return_to');
  const extensionId = searchParams.get('extension_id');

  useEffect(() => {
    // Track successful conversion
    console.log('Premium subscription successful');
    
    // If coming from extension, set a flag in chrome.storage to trigger refresh
    if (returnTo === 'extension' && extensionId && typeof chrome !== 'undefined' && chrome.storage) {
      chrome.storage.local.set({ 
        premiumStatusChanged: Date.now(),
        premiumActivated: true
      }, () => {
        console.log('Premium status flag set in chrome storage');
      });
    }
  }, [returnTo, extensionId]);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#feffdd',
      padding: '1rem 1rem 2rem 1rem',
      fontFamily: "'Kode Mono', monospace",
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      paddingTop: '4rem'
    }}>
      <div style={{
        maxWidth: '500px',
        width: '100%',
        background: '#fefddd',
        border: '4px solid #000',
        borderRadius: '16px',
        padding: '2.5rem 2rem',
        boxShadow: '12px 12px 0px #000',
        position: 'relative',
        textAlign: 'center'
      }}>
        {/* Success Badge */}
        <div style={{
          position: 'absolute',
          top: '-4px',
          right: '2.5rem',
          zIndex: 10,
          background: '#90EE90',
          border: '4px solid #000',
          borderTop: 'none',
          borderRadius: '0 0 8px 8px',
          padding: '1rem 1.25rem 0.6rem 1.25rem',
          fontWeight: 'bold',
          fontSize: '0.8rem',
          textTransform: 'uppercase',
          textAlign: 'center',
          boxShadow: '0 3px 5px rgba(0, 0, 0, 0.2)'
        }}>
          ✓ Activé
        </div>

        {/* Title */}
        <h1 style={{
          fontSize: 'clamp(1.5rem, 4vw, 2rem)',
          fontWeight: 'bold',
          marginBottom: '1rem',
          color: '#000',
          fontFamily: "'Kode Mono', monospace",
          lineHeight: '1.2'
        }}>
          {t.premium?.successTitle || 'Bienvenue à Premium !'}
        </h1>
        
        <p style={{
          fontSize: '0.9rem',
          color: '#666',
          marginBottom: '2rem',
          fontFamily: "'Kode Mono', monospace",
          lineHeight: '1.5'
        }}>
          {t.premium?.successMessage || 'Votre abonnement a été activé avec succès.'}
        </p>

        {/* Benefits List */}
        {returnTo === 'extension' ? (
          <ul style={{
            listStyle: 'none',
            padding: 0,
            margin: '0',
            fontFamily: "'Kode Mono', monospace",
            color: '#000',
            textAlign: 'left'
          }}>
            <li style={{ 
              marginBottom: '1.25rem', 
              lineHeight: '1.6', 
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.75rem'
            }}>
              <span style={{ 
                fontSize: '1.2rem', 
                color: '#4CAF50',
                flexShrink: 0,
                marginTop: '0.1rem'
              }}>✓</span>
              <span>{t.premium?.extensionBenefit || 'Vous pouvez maintenant utiliser toutes les fonctionnalités premium de l\'extension ColQuest Mapper'}</span>
            </li>
            <li style={{ 
              lineHeight: '1.6', 
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.75rem'
            }}>
              <span style={{ 
                fontSize: '1.2rem', 
                color: '#4CAF50',
                flexShrink: 0,
                marginTop: '0.1rem'
              }}>✓</span>
              <span>{t.premium?.appBenefit || 'Votre abonnement premium fonctionne aussi sur l\'application mobile. Toutes vos futures ascensions seront automatiquement synchronisées.'}</span>
            </li>
          </ul>
        ) : (
          <>
            <p style={{ 
              fontSize: '0.9rem', 
              marginBottom: '1.5rem',
              fontFamily: "'Kode Mono', monospace",
              color: '#000'
            }}>
              {t.premium?.downloadApp || 'Téléchargez l\'application ColQuest pour commencer votre aventure :'}
            </p>
            <div style={{ 
              display: 'flex', 
              gap: '1rem', 
              justifyContent: 'center',
              flexWrap: 'wrap',
              marginBottom: '2rem'
            }}>
              <a
                href="https://apps.apple.com/app/colquest"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '0.875rem 1.5rem',
                  background: '#f4c95d',
                  color: '#000',
                  border: '3px solid #000',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  fontFamily: "'Kode Mono', monospace",
                  transition: 'all 0.2s ease',
                  boxShadow: '4px 4px 0px #000'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translate(-2px, -2px)';
                  e.currentTarget.style.boxShadow = '6px 6px 0px #000';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '4px 4px 0px #000';
                }}
              >
                📱 iOS
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.colquest"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '0.875rem 1.5rem',
                  background: '#f4c95d',
                  color: '#000',
                  border: '3px solid #000',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  fontFamily: "'Kode Mono', monospace",
                  transition: 'all 0.2s ease',
                  boxShadow: '4px 4px 0px #000'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translate(-2px, -2px)';
                  e.currentTarget.style.boxShadow = '6px 6px 0px #000';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '4px 4px 0px #000';
                }}
              >
                🤖 Android
              </a>
            </div>
          </>
        )}

        {/* Support Note */}
        <p style={{
          fontSize: '0.75rem',
          color: '#666',
          fontFamily: "'Kode Mono', monospace",
          lineHeight: '1.5'
        }}>
          {t.premium?.supportNote || 'Besoin d\'aide ? Contactez-nous à'}{' '}
          <a 
            href="mailto:augustin@colquest.cc" 
            style={{ 
              color: '#000',
              textDecoration: 'underline',
              fontWeight: 'bold'
            }}
          >
            augustin@colquest.cc
          </a>
        </p>
      </div>
    </div>
  );
};

export default PremiumSuccessPage;
